import React, { useCallback, useEffect, useState } from "react";
import { getSupabase } from "../../lib/supabaseClient";
import { getSubmitIngredientNoteUrl } from "../../lib/checkoutConfig";
import { preflightIngredientNote, isIngredientNoteTargetAllowed } from "../../lib/ingredientNoteModeration";

/**
 * @param {{ ingredientId: string, ingredientName: string }} props
 */
export default function IngredientCommunityNotes({ ingredientId, ingredientName }) {
  const supabase = getSupabase();
  const [notes, setNotes] = useState([]);
  const [notesLoading, setNotesLoading] = useState(true);
  const [visible, setVisible] = useState(3);
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [location, setLocation] = useState("");
  const [comment, setComment] = useState("");
  const [ack, setAck] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [formError, setFormError] = useState(null);

  const loadApproved = useCallback(async () => {
    if (!supabase || !isIngredientNoteTargetAllowed(ingredientId)) {
      setNotes([]);
      setNotesLoading(false);
      return;
    }
    setNotesLoading(true);
    const { data, error } = await supabase
      .from("ingredient_notes")
      .select("id, display_name, location, comment, created_at, approved_at")
      .eq("ingredient_id", ingredientId)
      .eq("status", "approved")
      .order("approved_at", { ascending: false });
    if (error) {
      console.error(error);
      setNotes([]);
    } else {
      setNotes(data || []);
    }
    setNotesLoading(false);
  }, [supabase, ingredientId]);

  useEffect(() => {
    setDone(false);
    setVisible(3);
    loadApproved();
  }, [loadApproved]);

  const submit = async (e) => {
    e.preventDefault();
    setFormError(null);
    if (honeypot.trim()) {
      setDone(true);
      return;
    }
    if (!isIngredientNoteTargetAllowed(ingredientId)) {
      setFormError("This ingredient cannot accept notes here.");
      return;
    }
    if (!ack) {
      setFormError("Please tick the personal experience box.");
      return;
    }
    const pre = preflightIngredientNote({ displayName, location, comment });
    if (!pre.ok) {
      setFormError(pre.error);
      return;
    }
    setSubmitting(true);
    try {
      const url = getSubmitIngredientNoteUrl();
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ingredientId,
          displayName: displayName.trim(),
          email: email.trim(),
          location: location.trim(),
          comment: comment.trim(),
          personalExperienceAck: true,
          website: honeypot,
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFormError(payload.error || "Something went wrong. Please try again.");
        return;
      }
      setDone(true);
      setDisplayName("");
      setEmail("");
      setLocation("");
      setComment("");
      setAck(false);
    } catch {
      setFormError("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const shown = notes.slice(0, visible);
  const hasMore = notes.length > visible;

  return (
    <section id="community-notes" className="scroll-mt-28 max-w-3xl mb-12">
      <h2 className="font-serif text-2xl text-forest">Community notes</h2>
      <p className="mt-2 text-sm text-botanical/85 leading-relaxed">
        How do you use this ingredient in your routine? Share a short personal note. No medical claims, just your own kitchen or daily routine.
      </p>

      <div className="mt-6 space-y-4">
        {notesLoading ? (
          <p className="text-sm text-botanical/65">Loading notes…</p>
        ) : notes.length === 0 ? (
          <p className="rounded-2xl border border-cream bg-[#FAF7F0] px-4 py-3 text-sm text-botanical/80">
            No community notes yet. Be the first to share how you use this ingredient.
          </p>
        ) : (
          <>
            {shown.map((n) => (
              <article
                key={n.id}
                className="rounded-2xl border border-gold/25 bg-[#FAF7F0] px-4 py-3.5 text-sm text-forest shadow-[0_8px_28px_-14px_rgba(22,49,38,0.12)]"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-2 gap-y-1">
                  <span className="font-medium text-forest">{n.display_name}</span>
                  <time className="text-[11px] text-botanical/60 tabular-nums" dateTime={n.approved_at || n.created_at}>
                    {new Date(n.approved_at || n.created_at).toLocaleDateString("en-GB", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                  </time>
                </div>
                {n.location ? (
                  <p className="text-[11px] text-botanical/65 mt-0.5">{n.location}</p>
                ) : null}
                <p className="mt-2 text-botanical/90 leading-relaxed whitespace-pre-wrap">{n.comment}</p>
              </article>
            ))}
            {hasMore ? (
              <button
                type="button"
                onClick={() => setVisible((v) => v + 10)}
                className="text-xs font-medium text-forest underline hover:text-gold"
              >
                Show more notes
              </button>
            ) : null}
          </>
        )}
      </div>

      <div className="mt-10 rounded-2xl border border-cream bg-white p-5 sm:p-6">
        {done ? (
          <div className="space-y-3">
            <p className="text-sm text-forest">
              Thank you. Your note has been received and will be reviewed before it appears on the site.
            </p>
            <button
              type="button"
              onClick={() => setDone(false)}
              className="text-sm font-medium text-forest underline hover:text-gold"
            >
              Share another note
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <input
              type="text"
              name="website"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
              className="hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden
            />
            <div>
              <label htmlFor={`cn-name-${ingredientId}`} className="text-xs uppercase tracking-widest text-botanical/70">
                Display name <span className="text-gold">*</span>
              </label>
              <input
                id={`cn-name-${ingredientId}`}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value.slice(0, 40))}
                maxLength={40}
                required
                className="mt-1 w-full rounded-xl border border-cream bg-offwhite px-3 py-2 text-sm"
                placeholder="How you would like to appear"
                autoComplete="nickname"
              />
              <p className="text-[10px] text-botanical/55 mt-0.5">{displayName.length}/40</p>
            </div>
            <div>
              <label htmlFor={`cn-email-${ingredientId}`} className="text-xs uppercase tracking-widest text-botanical/70">
                Email <span className="text-gold">*</span> <span className="normal-case text-botanical/55">(never shown publicly)</span>
              </label>
              <input
                id={`cn-email-${ingredientId}`}
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1 w-full rounded-xl border border-cream bg-offwhite px-3 py-2 text-sm"
                autoComplete="email"
              />
            </div>
            <div>
              <label htmlFor={`cn-loc-${ingredientId}`} className="text-xs uppercase tracking-widest text-botanical/70">
                Location <span className="normal-case text-botanical/55">(optional)</span>
              </label>
              <input
                id={`cn-loc-${ingredientId}`}
                value={location}
                onChange={(e) => setLocation(e.target.value.slice(0, 40))}
                maxLength={40}
                className="mt-1 w-full rounded-xl border border-cream bg-offwhite px-3 py-2 text-sm"
                placeholder="e.g. Birmingham"
              />
            </div>
            <div>
              <label htmlFor={`cn-comment-${ingredientId}`} className="text-xs uppercase tracking-widest text-botanical/70">
                Your note <span className="text-gold">*</span>
              </label>
              <textarea
                id={`cn-comment-${ingredientId}`}
                value={comment}
                onChange={(e) => setComment(e.target.value.slice(0, 500))}
                maxLength={500}
                required
                rows={4}
                className="mt-1 w-full rounded-xl border border-cream bg-offwhite px-3 py-2 text-sm resize-y min-h-[100px]"
                placeholder={`How you use ${ingredientName} at home…`}
              />
              <p className="text-[10px] text-botanical/55 mt-0.5">{comment.length}/500</p>
            </div>
            <label className="flex items-start gap-2 text-xs text-botanical/85 leading-snug cursor-pointer">
              <input
                type="checkbox"
                checked={ack}
                onChange={(e) => setAck(e.target.checked)}
                className="mt-0.5 rounded border-cream"
                required
              />
              <span>I understand this is my personal experience and not medical advice.</span>
            </label>
            {formError ? <p className="text-sm text-red-800 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{formError}</p> : null}
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex rounded-full bg-forest text-cream px-6 py-2.5 text-sm font-medium hover:bg-botanical transition disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Share my note"}
            </button>
          </form>
        )}
      </div>
    </section>
  );
}
